import { useEffect, useRef, useState } from 'react';
import { LogIn, LogOut, UserRound, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { googleClientId } from '../utils/supabase';
import { appShellColors } from '../styles/appShell';
import { appCopy, type Locale } from '../i18n';

interface AuthButtonProps {
  locale: Locale;
  className?: string;
}

const GOOGLE_IDENTITY_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

function getUserLabel(user: NonNullable<ReturnType<typeof useAuth>['user']>) {
  const fullName = typeof user.user_metadata?.full_name === 'string' ? user.user_metadata.full_name : '';
  const name = typeof user.user_metadata?.name === 'string' ? user.user_metadata.name : '';
  return fullName || name || user.email || 'Account';
}

function loadGoogleIdentityScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google sign-in is not available in this environment.'));
  }

  if (window.google?.accounts.id) {
    return Promise.resolve();
  }

  const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${GOOGLE_IDENTITY_SCRIPT_SRC}"]`);
  if (existingScript) {
    return new Promise<void>((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Google sign-in script failed to load.')), { once: true });
    });
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GOOGLE_IDENTITY_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Google sign-in script failed to load.'));
    document.head.appendChild(script);
  });
}

export function AuthButton({ locale, className = '' }: AuthButtonProps) {
  const { clearError, error, isConfigured, isGoogleConfigured, signInWithGoogleToken, signOut, status, user } = useAuth();
  const copy = appCopy[locale].auth;
  const isLoading = status === 'loading';
  const [panelOpen, setPanelOpen] = useState(false);
  const [googleScriptError, setGoogleScriptError] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const googleButtonRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!error) {
      return undefined;
    }

    const timeoutId = window.setTimeout(clearError, 2400);
    return () => window.clearTimeout(timeoutId);
  }, [clearError, error]);

  useEffect(() => {
    if (!panelOpen) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setPanelOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPanelOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [panelOpen]);

  useEffect(() => {
    if (!panelOpen || !isGoogleConfigured || !googleButtonRef.current) {
      return undefined;
    }

    let isActive = true;
    setGoogleScriptError(false);

    loadGoogleIdentityScript()
      .then(() => {
        if (!isActive || !googleButtonRef.current || !window.google?.accounts.id) {
          return;
        }

        googleButtonRef.current.replaceChildren();
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: (response) => {
            void signInWithGoogleToken(response.credential ?? '');
          },
          ux_mode: 'popup',
        });
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          locale: locale === 'zh' ? 'zh-CN' : 'en',
          shape: 'rectangular',
          size: 'large',
          text: 'continue_with',
          theme: 'outline',
          type: 'standard',
          width: 288,
        });
      })
      .catch(() => {
        if (isActive) {
          setGoogleScriptError(true);
        }
      });

    return () => {
      isActive = false;
    };
  }, [isGoogleConfigured, locale, panelOpen, signInWithGoogleToken]);

  if (status === 'signed-in' && user) {
    const userLabel = getUserLabel(user);

    return (
      <div className={`relative inline-flex min-h-11 max-w-full items-center rounded-lg border ${className}`}>
        <span
          className="inline-flex min-h-11 min-w-0 flex-1 items-center gap-2 px-3 text-sm font-medium"
          style={{
            backgroundColor: appShellColors.surface,
            borderColor: appShellColors.border,
            color: appShellColors.textPrimary,
          }}
          title={copy.signedInAs(userLabel)}
        >
          <UserRound size={16} aria-hidden="true" />
          <span className="truncate">{userLabel}</span>
        </span>
        <button
          type="button"
          onClick={signOut}
          aria-label={copy.signOut}
          className="inline-flex min-h-11 items-center gap-2 border-l px-3 text-sm font-medium transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{
            backgroundColor: appShellColors.surface,
            borderColor: appShellColors.border,
            color: appShellColors.textSecondary,
            ['--tw-ring-color' as string]: appShellColors.focusRing,
          }}
        >
          <LogOut size={16} aria-hidden="true" />
          <span className="hidden sm:inline">{copy.signOut}</span>
        </button>
        {error && (
          <p
            role="status"
            className="absolute right-0 top-[calc(100%+8px)] z-50 w-64 rounded-lg border px-3 py-2 text-xs shadow-lg"
            style={{
              backgroundColor: appShellColors.surface,
              borderColor: '#fecaca',
              color: '#991b1b',
            }}
          >
            {copy.actionFailed}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={panelRef}>
      <button
        type="button"
        onClick={() => setPanelOpen((current) => !current)}
        aria-expanded={panelOpen}
        aria-haspopup="dialog"
        title={!isConfigured ? copy.configureHint : undefined}
        className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        style={{
          backgroundColor: appShellColors.surface,
          borderColor: appShellColors.border,
          color: appShellColors.textPrimary,
          ['--tw-ring-color' as string]: appShellColors.focusRing,
        }}
      >
        <LogIn size={16} aria-hidden="true" />
        {copy.entry}
      </button>
      {panelOpen && (
        <div
          role="dialog"
          aria-label={copy.dialogTitle}
          className="absolute right-0 top-[calc(100%+8px)] z-50 w-80 rounded-lg border p-4 shadow-xl"
          style={{
            backgroundColor: appShellColors.surface,
            borderColor: appShellColors.border,
            color: appShellColors.textPrimary,
          }}
        >
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold">{copy.dialogTitle}</h2>
              <p className="mt-1 text-xs" style={{ color: appShellColors.textMuted }}>
                {copy.dialogDescription}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setPanelOpen(false)}
              aria-label={copy.closeDialog}
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{ color: appShellColors.textSecondary, ['--tw-ring-color' as string]: appShellColors.focusRing }}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
          {isGoogleConfigured ? (
            <div className="min-h-11">
              <div ref={googleButtonRef} className={isLoading ? 'pointer-events-none opacity-60' : undefined} />
              {googleScriptError && (
                <p className="mt-3 text-xs" style={{ color: '#991b1b' }}>
                  {copy.googleUnavailable}
                </p>
              )}
            </div>
          ) : (
            <button
              type="button"
              disabled
              className="flex min-h-11 w-full cursor-not-allowed items-center justify-center gap-2 rounded-lg border px-3 text-sm font-medium opacity-60"
              style={{
                backgroundColor: appShellColors.surface,
                borderColor: appShellColors.border,
                color: appShellColors.textPrimary,
              }}
            >
              <span
                aria-hidden="true"
                className="flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold"
                style={{ backgroundColor: appShellColors.primary, color: appShellColors.onPrimary }}
              >
                G
              </span>
              {copy.signInWithGoogle}
            </button>
          )}
          {!isConfigured && (
            <p className="mt-3 text-xs" style={{ color: appShellColors.textMuted }}>
              {copy.configureHint}
            </p>
          )}
        </div>
      )}
      {error && (
        <p
          role="status"
          className="absolute right-0 top-[calc(100%+8px)] z-50 w-72 rounded-lg border px-3 py-2 text-xs shadow-lg"
          style={{
            backgroundColor: appShellColors.surface,
            borderColor: '#fecaca',
            color: '#991b1b',
          }}
        >
          {copy.actionFailed}
        </p>
      )}
    </div>
  );
}
